import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface Project {
  _id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  age: string;
  category: string;
  platform: string;
  bannerImage: string;
  isActive: boolean;
  steps: string[];
  content: any;
  requirements: {
    hardware: string[];
    software: string[];
  };
  learning: string[];
  educatorInfo: {
    downloadLink: string;
    printerFriendlyLink: string;
  };
  createdAt: string;
  updatedAt: string;
}

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterDifficulty, setFilterDifficulty] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const categories = ['All', 'LEDs', 'Games', 'Robotics', 'Sensors', 'Music', 'IoT', 'Mobile', 'Nature', 'AI & Data'];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard'];
  const platforms = ['Arduino', 'Raspberry Pi', 'ESP32', 'ESP8266', 'Scratch', 'Python', 'JavaScript', 'Micro:bit'];

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'Easy' as 'Easy' | 'Medium' | 'Hard',
    age: '',
    category: '',
    platform: '',
    bannerImage: '',
    isActive: true,
    steps: ['Introduction'],
    content: {
      'Introduction': {
        title: 'Introduction',
        description: '',
        subheading: 'What you will make',
        subdescription: ''
      }
    },
    requirements: {
      hardware: [''],
      software: ['']
    },
    learning: [''],
    educatorInfo: {
      downloadLink: '',
      printerFriendlyLink: ''
    }
  });

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/highcode/projects`
      );

      if (!response.ok) throw new Error("Failed to fetch projects");

      const data = await response.json();
      setProjects(data.projects || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching projects:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'All' || project.category === filterCategory;
    const matchesDifficulty = filterDifficulty === 'All' || project.difficulty === filterDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const toggleProjectStatus = async (id: string) => {
    try {
      setIsUpdating(true);
      const project = projects.find(p => p._id === id);
      if (!project) return;

      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/projects/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: !project.isActive }),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update project status");

      await fetchProjects(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update project status");
      console.error("Error updating project status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteProject = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      setIsDeleting(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/projects/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to delete project");

      await fetchProjects(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete project");
      console.error("Error deleting project:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      
      const url = selectedProject 
        ? `${process.env.REACT_APP_API_URL}/highcode/projects/${selectedProject._id}`
        : `${process.env.REACT_APP_API_URL}/highcode/projects`;
      
      const method = selectedProject ? 'PATCH' : 'POST';
      
      const response = await adminFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response) return;
      if (!response.ok) throw new Error(`Failed to ${selectedProject ? 'update' : 'create'} project`);

      await fetchProjects(); // Refresh the list
      handleCloseModals();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${selectedProject ? 'update' : 'create'} project`);
      console.error("Error saving project:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle opening modals
  const handleAddProject = () => {
    setFormData({
      title: '',
      description: '',
      difficulty: 'Easy',
      age: '',
      category: '',
      platform: '',
      bannerImage: '',
      isActive: true,
      steps: ['Introduction'],
      content: {
        'Introduction': {
          title: 'Introduction',
          description: '',
          subheading: 'What you will make',
          subdescription: ''
        }
      },
      requirements: {
        hardware: [''],
        software: ['']
      },
      learning: [''],
      educatorInfo: {
        downloadLink: '',
        printerFriendlyLink: ''
      }
    });
    setSelectedProject(null);
    setShowAddModal(true);
  };

  const handleEditProject = (project: Project) => {
    setFormData({
      title: project.title,
      description: project.description,
      difficulty: project.difficulty,
      age: project.age,
      category: project.category,
      platform: project.platform,
      bannerImage: project.bannerImage,
      isActive: project.isActive,
      steps: project.steps || ['Introduction'],
      content: project.content || {
        'Introduction': {
          title: 'Introduction',
          description: '',
          subheading: 'What you will make',
          subdescription: ''
        }
      },
      requirements: project.requirements || {
        hardware: [''],
        software: ['']
      },
      learning: project.learning || [''],
      educatorInfo: project.educatorInfo || {
        downloadLink: '',
        printerFriendlyLink: ''
      }
    });
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setSelectedProject(null);
    setFormData({
      title: '',
      description: '',
      difficulty: 'Easy',
      age: '',
      category: '',
      platform: '',
      bannerImage: '',
      isActive: true,
      steps: ['Introduction'],
      content: {
        'Introduction': {
          title: 'Introduction',
          description: '',
          subheading: 'What you will make',
          subdescription: ''
        }
      },
      requirements: {
        hardware: [''],
        software: ['']
      },
      learning: [''],
      educatorInfo: {
        downloadLink: '',
        printerFriendlyLink: ''
      }
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Projects</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchProjects}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects Management</h1>
          <p className="text-gray-600">Manage all 50+ STEM projects for High Code</p>
        </div>
        <button
          onClick={handleAddProject}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-plus mr-2"></i>
          Add New Project
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search projects..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('All');
                setFilterDifficulty('All');
              }}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Difficulty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <tr key={project._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={project.bannerImage}
                          alt={project.title}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{project.title}</div>
                        <div className="text-sm text-gray-500">{project.description}</div>
                        <div className="text-xs text-gray-400">Age: {project.age}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {project.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      project.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                      project.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {project.difficulty}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.platform}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleProjectStatus(project._id)}
                      disabled={isUpdating}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        project.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                    >
                      {project.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditProject(project)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit Project"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        onClick={() => handleViewProject(project)}
                        className="text-green-600 hover:text-green-900"
                        title="View Project"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button 
                        onClick={() => deleteProject(project._id)}
                        disabled={isDeleting}
                        className={`text-red-600 hover:text-red-900 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Delete Project"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-code text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Projects</p>
              <p className="text-2xl font-semibold text-gray-900">{projects.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-check-circle text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Projects</p>
              <p className="text-2xl font-semibold text-gray-900">
                {projects.filter(p => p.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-tags text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Categories</p>
              <p className="text-2xl font-semibold text-gray-900">
                {new Set(projects.map(p => p.category)).size}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-microchip text-2xl text-orange-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Platforms</p>
              <p className="text-2xl font-semibold text-gray-900">
                {new Set(projects.map(p => p.platform)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Project Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedProject ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button
                onClick={handleCloseModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age Group *
                    </label>
                    <input
                      type="text"
                      value={formData.age}
                      onChange={(e) => setFormData({...formData, age: e.target.value})}
                      placeholder="e.g., 4+, 6-12, 14-18"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.filter(cat => cat !== 'All').map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty *
                    </label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData({...formData, difficulty: e.target.value as 'Easy' | 'Medium' | 'Hard'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Platform *
                    </label>
                    <select
                      value={formData.platform}
                      onChange={(e) => setFormData({...formData, platform: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Platform</option>
                      {platforms.map(platform => (
                        <option key={platform} value={platform}>{platform}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banner Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.bannerImage}
                      onChange={(e) => setFormData({...formData, bannerImage: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active Project
                  </label>
                </div>
              </div>

              {/* Project Steps */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Steps</h3>
                <div className="space-y-3">
                  {formData.steps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700 w-8">{index + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => {
                          const newSteps = [...formData.steps];
                          newSteps[index] = e.target.value;
                          setFormData({...formData, steps: newSteps});
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Step title"
                      />
                      {formData.steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSteps = formData.steps.filter((_, i) => i !== index);
                            setFormData({...formData, steps: newSteps});
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, steps: [...formData.steps, '']})}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <i className="fas fa-plus mr-1"></i>Add Step
                  </button>
                </div>
              </div>

              {/* Requirements */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hardware</label>
                    <div className="space-y-2">
                      {formData.requirements.hardware.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newHardware = [...formData.requirements.hardware];
                              newHardware[index] = e.target.value;
                              setFormData({...formData, requirements: {...formData.requirements, hardware: newHardware}});
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Hardware item"
                          />
                          {formData.requirements.hardware.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newHardware = formData.requirements.hardware.filter((_, i) => i !== index);
                                setFormData({...formData, requirements: {...formData.requirements, hardware: newHardware}});
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, requirements: {...formData.requirements, hardware: [...formData.requirements.hardware, '']}})}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <i className="fas fa-plus mr-1"></i>Add Hardware Item
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Software</label>
                    <div className="space-y-2">
                      {formData.requirements.software.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newSoftware = [...formData.requirements.software];
                              newSoftware[index] = e.target.value;
                              setFormData({...formData, requirements: {...formData.requirements, software: newSoftware}});
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Software item"
                          />
                          {formData.requirements.software.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newSoftware = formData.requirements.software.filter((_, i) => i !== index);
                                setFormData({...formData, requirements: {...formData.requirements, software: newSoftware}});
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, requirements: {...formData.requirements, software: [...formData.requirements.software, '']}})}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <i className="fas fa-plus mr-1"></i>Add Software Item
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Learning Goals */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Goals</h3>
                <div className="space-y-2">
                  {formData.learning.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const newLearning = [...formData.learning];
                          newLearning[index] = e.target.value;
                          setFormData({...formData, learning: newLearning});
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Learning goal"
                      />
                      {formData.learning.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newLearning = formData.learning.filter((_, i) => i !== index);
                            setFormData({...formData, learning: newLearning});
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, learning: [...formData.learning, '']})}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <i className="fas fa-plus mr-1"></i>Add Learning Goal
                  </button>
                </div>
              </div>

              {/* Educator Info */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Educator Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Download Link
                    </label>
                    <input
                      type="url"
                      value={formData.educatorInfo.downloadLink}
                      onChange={(e) => setFormData({...formData, educatorInfo: {...formData.educatorInfo, downloadLink: e.target.value}})}
                      placeholder="https://example.com/download"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Printer Friendly Link
                    </label>
                    <input
                      type="url"
                      value={formData.educatorInfo.printerFriendlyLink}
                      onChange={(e) => setFormData({...formData, educatorInfo: {...formData.educatorInfo, printerFriendlyLink: e.target.value}})}
                      placeholder="https://example.com/printer-friendly"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`px-4 py-2 text-white rounded-md transition-colors ${
                    isSaving 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isSaving ? 'Saving...' : (selectedProject ? 'Update Project' : 'Create Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Project Modal */}
      {showViewModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Project Details</h2>
              <button
                onClick={handleCloseModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <img
                  src={selectedProject.bannerImage}
                  alt={selectedProject.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedProject.title}</h3>
                  <p className="text-sm text-gray-600">Age: {selectedProject.age}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-gray-700">{selectedProject.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Category</h4>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {selectedProject.category}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Difficulty</h4>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedProject.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                    selectedProject.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedProject.difficulty}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Platform</h4>
                  <p className="text-gray-700">{selectedProject.platform}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Status</h4>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedProject.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedProject.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleCloseModals}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
